import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sun,
  BatteryFull,
  Cpu,
  Lightning,
  CaretRight,
  FileText,
  Files,
  Megaphone,
  Path,
  BookOpen,
  MapTrifold,
  YoutubeLogo,
  MagnifyingGlass,
  Drop,
  Truck,
} from "@phosphor-icons/react";
import { ASSETS } from "@/lib/assets";
import { useCustomer } from "@/context/CustomerContext";
import { getErrorMessage, API } from "@/lib/api";

const SPEC_ROWS = [
  { key: "solar_configuration", fallback: "solar", label: "Solar", icon: Sun },
  { key: "battery_configuration", fallback: "battery", label: "Battery", icon: BatteryFull },
  { key: "management_system", label: "Management", icon: Cpu },
  { key: "inverter_charger", fallback: "inverter", label: "Inverter / Charger", icon: Lightning },
  { key: "axle", label: "Axle", icon: Truck },
  { key: "water_tank_capacity", label: "Plumbing", icon: Drop },
];

const QUICK_LINKS = [
  { label: "User Manuals", icon: FileText, to: "/manuals" },
  { label: "Documents", icon: Files, to: "/documents" },
  { label: "Service Bulletins", icon: Megaphone, to: "/service-bulletins" },
  { label: "Wiring Diagrams", icon: Path, to: "/wiring-diagrams" },
  { label: "System Guides", icon: BookOpen, to: "/guides" },
  { label: "Repair Network", icon: MapTrifold, to: "/repairers" },
  { label: "Video Tutorials", icon: YoutubeLogo, to: "/videos" },
];

export function CaravanPanel() {
  const { caravan, lookupCaravan, clearCaravan } = useCustomer();
  const [chassis, setChassis] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const doLookup = async (e) => {
    e?.preventDefault();
    if (!chassis.trim()) return;
    setLoading(true);
    try {
      await lookupCaravan(chassis);
      toast.success("Caravan found");
      setChassis("");
    } catch (err) {
      toast.error(getErrorMessage(err, "No caravan found for that chassis number"));
    } finally {
      setLoading(false);
    }
  };

  if (!caravan) {
    return (
      <div data-testid="caravan-panel">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44] mb-4">Your Caravan</div>
        <div className="bg-[#171717] rounded-xl border border-neutral-800 overflow-hidden">
          <div className="relative h-36 bg-gradient-to-b from-neutral-100 to-neutral-300 flex items-center justify-center">
            <img src={ASSETS.caravan} alt="On The Move Adventure Black Edition" className="h-full w-full object-contain p-2" />
            <div className="absolute bottom-2 left-3">
              <div className="font-heading text-sm font-bold text-[#050505]/80 uppercase tracking-tight leading-none">Adventure</div>
              <div className="text-[0.55rem] tracking-[0.25em] text-[#050505]/60 uppercase">Black Edition</div>
            </div>
          </div>
          <div className="p-5">
            <p className="text-sm text-neutral-400 mb-4">
              Enter your chassis number so Nick can tailor answers to your exact caravan configuration.
            </p>
            <form onSubmit={doLookup} className="space-y-3">
              <div className="flex items-center gap-2 bg-black/40 border border-neutral-700 rounded-lg px-3 py-2 focus-within:border-[#73bf44] transition-colors">
                <MagnifyingGlass size={16} className="text-neutral-500" />
                <input
                  data-testid="caravan-panel-chassis-input"
                  value={chassis}
                  onChange={(e) => setChassis(e.target.value)}
                  placeholder="e.g. OTM123456"
                  className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600 font-mono"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="caravan-panel-lookup-button"
                className="w-full bg-[#73bf44] text-[#050505] rounded-full font-semibold text-sm py-2.5 hover:bg-[#5fa338] transition-colors disabled:opacity-60"
              >
                {loading ? "Looking up..." : "Find My Caravan"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="caravan-panel">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44]">Your Caravan</span>
        <button
          onClick={() => { clearCaravan(); toast.message("Caravan cleared"); }}
          className="text-[0.65rem] text-neutral-500 hover:text-white transition-colors"
          data-testid="caravan-panel-change-button"
        >
          Change
        </button>
      </div>
      <div className="bg-[#171717] rounded-xl border border-neutral-800 overflow-hidden">
        <div className="relative h-32 bg-gradient-to-b from-neutral-100 to-neutral-300 flex items-center justify-center">
          <img
            src={caravan.model_photo_id ? `${API}/model-photos/${caravan.model_photo_id}/file` : ASSETS.caravan}
            onError={(e) => { e.currentTarget.src = ASSETS.caravan; }}
            alt={caravan.model}
            className="h-full w-full object-contain p-2"
          />
        </div>
        <div className="p-5">
          <div className="mb-4">
            <h3 className="font-heading text-2xl font-bold tracking-tight text-white uppercase">{caravan.model || "Caravan"}</h3>
            <div className="text-xs tracking-[0.2em] text-neutral-500 uppercase">{[caravan.exterior_colour, caravan.decal_colour && `${caravan.decal_colour} decals`].filter(Boolean).join(" · ")}</div>
          </div>
          <div className="divide-y divide-neutral-800">
            {SPEC_ROWS.map((row) => {
              const val = caravan[row.key] || (row.fallback && caravan[row.fallback]);
              if (!val) return null;
              const Icon = row.icon;
              return (
                <div key={row.key} className="flex items-center gap-3 py-2.5">
                  <div className="w-8 h-8 rounded-lg bg-black/40 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-[#73bf44]" weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white line-clamp-2">{val}</div>
                    <div className="text-[0.65rem] uppercase tracking-wider text-neutral-500">{row.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => navigate("/my-caravan")}
            data-testid="caravan-panel-full-specs-button"
            className="mt-4 w-full flex items-center justify-between rounded-lg border border-neutral-700 px-4 py-2.5 text-sm text-neutral-200 hover:border-[#73bf44] hover:text-white transition-colors group"
          >
            View Full Specifications
            <CaretRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function QuickLinks() {
  const navigate = useNavigate();
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44] mb-3">Quick Links</div>
      <div className="flex flex-col">
        {QUICK_LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <button
              key={l.label}
              onClick={() => navigate(l.to)}
              data-testid={`quicklink-${l.to.replace("/", "")}`}
              className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/[0.04] text-neutral-300 hover:text-white transition-colors group text-left"
            >
              <Icon size={20} weight="duotone" className="text-neutral-400 group-hover:text-[#73bf44] transition-colors" />
              <span className="text-sm flex-1">{l.label}</span>
              <CaretRight size={14} className="text-neutral-600 group-hover:text-white transition-colors" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RightRail() {
  return (
    <aside className="hidden xl:block w-80 shrink-0 bg-[#0F0F0F] border-l border-neutral-800 overflow-y-auto py-6 px-6 space-y-8">
      <CaravanPanel />
      <QuickLinks />
    </aside>
  );
}
